// GuildStatsAdminPage.tsx - Admin analytics.
//
// Two audiences, two views (segmented control at the top):
//   * Growth  - a marketing/leadership read on the product: headline numbers,
//               a plain-language acquisition -> activation funnel, the key
//               conversion rates, time-to-value, and highlight cards.
//   * Guilds  - a searchable/sortable directory of every guild with a
//               prominent "View as guild" action so admins can drop into any
//               guild's planner in admin mode.
//
// Data comes from two existing endpoints, fetched together:
//   GET /admin/guild-stats  -> per-guild stats (members, raids, discord, ...)
//   GET /admin/funnel       -> acquisition + activation funnel + per-guild stage
// The previous, denser version is kept alongside as GuildStatsAdminPage.tsx.bak.

import React, { useEffect, useMemo, useState } from "react";
import { CreateRaidGuild } from "./CreateRaidPage";

/* ===================== TYPES ===================== */

interface GuildStat {
  id: string;
  name: string;
  tag: string | null;
  slug: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  game_id: string | null;
  server_name: string | null;
  faction: string | null;
  logo_url: string | null;
  region: string | null;
  game_name: string | null;
  owner_name: string | null;
  member_count: number;
  raid_count: number;
  published_raid_count: number;
  latest_raid_date: string | null;
  soft_reserve_count: number;
  discord_bot_enabled: boolean;
  discord_server_id: string | null;
  character_count: number;
  has_recent_activity: boolean;
}

interface FunnelStep {
  key: string;
  label: string;
  scope: string;
  count: number;
}

interface FunnelTiming {
  key: string;
  label: string;
  median_hours: number | null;
  n: number;
}

interface FunnelTiers {
  signups: number;
  guilds: number;
  qualified: number;
  activated: number;
  successful: number;
  retained: number;
}

interface FunnelGuild {
  guild_id: string;
  name: string;
  tag: string | null;
  is_active: boolean;
  created_at: string;
  owner_signup_at: string | null;
  invite_visited_at: string | null;
  first_join_at: string | null;
  fifth_join_at: string | null;
  tenth_join_at: string | null;
  members_joined: number;
  availability_at: string | null;
  roster_at: string | null;
  first_raid_at: string | null;
  second_raid_at: string | null;
  raids_scheduled: number;
  first_completed_at: string | null;
  second_completed_at: string | null;
  stage: string;
  last_activity_at: string | null;
}

interface FunnelPlayers {
  availability: number;
  joined_guild: number;
  raided: number;
  raided_again: number;
}

interface FunnelData {
  fetched_at: string;
  steps: FunnelStep[];
  timings: FunnelTiming[];
  tiers: FunnelTiers;
  players: FunnelPlayers;
  guilds: FunnelGuild[];
}

/* ===================== HELPERS ===================== */

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "\u00b7";
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function fmtNum(n: number): string {
  if (n >= 10000) return `${(n / 1000).toFixed(n >= 100000 ? 0 : 1)}k`;
  return n.toLocaleString("en-GB");
}

function fmtDuration(hours: number | null): string {
  if (hours == null) return "\u00b7";
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  if (hours < 48) return `${hours < 10 ? hours.toFixed(1) : Math.round(hours)} hrs`;
  return `${(hours / 24).toFixed(1)} days`;
}

function daysSince(dateStr: string | null): number | null {
  if (!dateStr) return null;
  return (Date.now() - new Date(dateStr).getTime()) / 86400000;
}

// Conversion vs a base. Over 100% means mismatched history (a backfilled step
// after a newly-tracked one), which is noise - hide it.
function convPct(numer: number, denom: number): number | null {
  if (denom <= 0) return null;
  const pct = Math.round((numer / denom) * 100);
  return pct > 100 ? null : pct;
}

// Steps that only started being recorded when funnel tracking shipped
// (16 Jul 2026). They render as "new" until real data accumulates.
const NO_HISTORY_STEPS = new Set(["invite_visited", "roster"]);

/* ===================== ACCENTS ===================== */
// Full class strings (not built dynamically) so Tailwind keeps them.

type Accent = "indigo" | "sky" | "cyan" | "amber" | "emerald" | "violet" | "rose";

const ACCENT: Record<
  Accent,
  { text: string; dot: string; bar: string; soft: string; ring: string }
> = {
  indigo: {
    text: "text-indigo-700 dark:text-indigo-300",
    dot: "bg-indigo-400",
    bar: "from-indigo-500 to-indigo-400",
    soft: "bg-indigo-500/10 border-indigo-500/25 text-indigo-700 dark:text-indigo-300",
    ring: "ring-indigo-400/50",
  },
  sky: {
    text: "text-sky-700 dark:text-sky-300",
    dot: "bg-sky-400",
    bar: "from-sky-500 to-sky-400",
    soft: "bg-sky-500/10 border-sky-500/25 text-sky-700 dark:text-sky-300",
    ring: "ring-sky-400/50",
  },
  cyan: {
    text: "text-cyan-700 dark:text-cyan-300",
    dot: "bg-cyan-400",
    bar: "from-cyan-500 to-cyan-400",
    soft: "bg-cyan-500/10 border-cyan-500/25 text-cyan-700 dark:text-cyan-300",
    ring: "ring-cyan-400/50",
  },
  amber: {
    text: "text-amber-700 dark:text-amber-300",
    dot: "bg-amber-400",
    bar: "from-amber-500 to-amber-400",
    soft: "bg-amber-500/10 border-amber-500/25 text-amber-700 dark:text-amber-300",
    ring: "ring-amber-400/50",
  },
  emerald: {
    text: "text-emerald-700 dark:text-emerald-300",
    dot: "bg-emerald-400",
    bar: "from-emerald-500 to-emerald-400",
    soft: "bg-emerald-500/10 border-emerald-500/25 text-emerald-700 dark:text-emerald-300",
    ring: "ring-emerald-400/50",
  },
  violet: {
    text: "text-violet-700 dark:text-violet-300",
    dot: "bg-violet-400",
    bar: "from-violet-500 to-violet-400",
    soft: "bg-violet-500/10 border-violet-500/25 text-violet-700 dark:text-violet-300",
    ring: "ring-violet-400/50",
  },
  rose: {
    text: "text-rose-700 dark:text-rose-300",
    dot: "bg-rose-400",
    bar: "from-rose-500 to-rose-400",
    soft: "bg-rose-500/10 border-rose-500/25 text-rose-700 dark:text-rose-300",
    ring: "ring-rose-400/50",
  },
};

/* ===================== SMALL UI ATOMS ===================== */

// Hover "?" with a definition. Kept for depth, but the UI reads fine without it.
function Info({ text, wide }: { text: string; wide?: boolean }) {
  return (
    <span className="relative inline-flex group/help align-middle ml-1.5">
      <span className="w-4 h-4 inline-flex items-center justify-center rounded-full bg-[var(--c-sunken-2)] text-[var(--c-ink-3)] text-[10px] font-bold cursor-help transition group-hover/help:bg-[var(--c-ink-4)] group-hover/help:text-[var(--c-ink)]">
        ?
      </span>
      <span
        className={`pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover/help:block ${
          wide ? "w-72" : "w-56"
        } bg-[var(--c-sunken)] border border-[var(--c-line-strong)] rounded-lg px-3 py-2 text-[11px] leading-snug text-[var(--c-ink)] normal-case tracking-normal font-normal text-left shadow-xl z-30`}
      >
        {text}
      </span>
    </span>
  );
}

function Panel({
  title,
  hint,
  right,
  children,
  className,
}: {
  title?: string;
  hint?: string;
  right?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section
      className={`rounded-2xl border border-[var(--c-line)] bg-[var(--c-bg-veil)] p-5 ${className || ""}`}
    >
      {(title || right) && (
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            {title && (
              <h3 className="text-sm font-semibold text-[var(--c-ink)] flex items-center">
                {title}
                {hint && <Info text={hint} wide />}
              </h3>
            )}
          </div>
          {right}
        </div>
      )}
      {children}
    </section>
  );
}

/* ===================== KPI CARDS ===================== */

function Kpi({
  label,
  value,
  sub,
  accent,
  icon,
}: {
  label: string;
  value: string;
  sub?: React.ReactNode;
  accent: Accent;
  icon: React.ReactNode;
}) {
  const a = ACCENT[accent];
  return (
    <div className="relative overflow-hidden rounded-2xl border border-[var(--c-line)] bg-[var(--c-bg-veil)] p-5">
      <div
        className={`absolute -right-6 -top-6 w-24 h-24 rounded-full blur-2xl bg-gradient-to-br ${a.bar} opacity-20`}
      />
      <div className="relative">
        <div className="flex items-center gap-2 text-[var(--c-ink-3)] text-xs font-medium">
          <span className={a.text}>{icon}</span>
          {label}
        </div>
        <div className="mt-2 text-3xl font-bold text-[var(--c-ink)] tracking-tight">{value}</div>
        {sub && <div className="mt-1 text-xs text-[var(--c-ink-3)]">{sub}</div>}
      </div>
    </div>
  );
}

// Compact secondary metric (feature adoption, totals) shown under the KPIs.
function MiniStat({
  label,
  value,
  accent,
  icon,
}: {
  label: string;
  value: string;
  accent: Accent;
  icon: React.ReactNode;
}) {
  const a = ACCENT[accent];
  return (
    <div className="flex items-center gap-3 rounded-xl border border-[var(--c-line)] bg-[var(--c-bg-veil)] px-4 py-3">
      <span className={`shrink-0 ${a.text}`}>{icon}</span>
      <div className="min-w-0">
        <div className="text-lg font-bold text-[var(--c-ink)] leading-none">{value}</div>
        <div className="text-[11px] text-[var(--c-ink-3)] mt-1 truncate">{label}</div>
      </div>
    </div>
  );
}

// The sign-up activation story. Creating a guild was never the expected path
// for most players, so a bare "1% created a guild" reads as a failure when it
// isn't. We lead with the share who got into a guild at all (founded OR
// joined) and still spotlight founders as their own number, because that small
// group is what the whole system is built on.
function GuildActivationCard({
  signups,
  founders,
  members,
}: {
  signups: number;
  founders: number;
  members: number;
}) {
  const founderPct = signups > 0 ? (founders / signups) * 100 : 0;
  const memberPct = signups > 0 ? (members / signups) * 100 : 0;
  const engaged = Math.min(100, Math.round(founderPct + memberPct));
  const label = (pct: number, count: number) =>
    count === 0 ? "0%" : pct < 1 ? "<1%" : `${Math.round(pct)}%`;
  // Keep the founder sliver visible even at ~1%.
  const founderW = founders > 0 ? Math.max(founderPct, 2) : 0;
  const memberW = Math.max(0, Math.min(memberPct, 100 - founderW));
  return (
    <div className="rounded-2xl border border-[var(--c-line)] bg-[var(--c-bg-veil)] p-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs text-[var(--c-ink-3)]">Sign-ups active in a guild</div>
          <div className="mt-1 flex items-baseline gap-1">
            <span className="text-3xl font-bold text-cyan-700 dark:text-cyan-300">{engaged}</span>
            <span className="text-lg font-semibold text-cyan-700 dark:text-cyan-300">%</span>
          </div>
          <div className="mt-1 text-[11px] text-[var(--c-ink-3)]">
            founded or joined a guild, out of {fmtNum(signups)} sign-ups
          </div>
        </div>
        {/* Founders: small in number, but the backbone of the whole system */}
        <div className="flex items-center gap-2.5 rounded-xl border border-indigo-400/30 bg-indigo-500/[0.08] px-3.5 py-2.5">
          <span className="text-indigo-700 dark:text-indigo-300">
            <Icon d={I.flag} className="w-5 h-5" />
          </span>
          <div>
            <div className="text-lg font-bold text-[var(--c-ink)] leading-none">
              {fmtNum(founders)}
              <span className="ml-1.5 text-xs font-medium text-[var(--c-ink-3)]">founded a guild</span>
            </div>
            <div className="text-[11px] text-indigo-800/70 dark:text-indigo-200/70 mt-1">
              The leaders the platform runs on &middot; {label(founderPct, founders)} of sign-ups
            </div>
          </div>
        </div>
      </div>

      {/* Stacked bar: founders + members = the engaged share */}
      <div className="mt-3 h-2.5 rounded-full bg-[var(--c-sunken)] overflow-hidden flex">
        <div
          className="h-full bg-gradient-to-r from-indigo-500 to-indigo-400"
          style={{ width: `${founderW}%` }}
          title="Founded a guild"
        />
        <div
          className="h-full bg-gradient-to-r from-cyan-500 to-cyan-400"
          style={{ width: `${memberW}%` }}
          title="Joined a guild"
        />
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-[var(--c-ink-3)]">
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-indigo-400" />
          Founders {label(founderPct, founders)}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-cyan-400" />
          Members {label(memberPct, members)}
        </span>
      </div>
    </div>
  );
}

// A single conversion-rate readout used in the "how the product converts" strip.
function RateCard({
  label,
  pct,
  detail,
  accent,
}: {
  label: string;
  pct: number | null;
  detail: string;
  accent: Accent;
}) {
  const a = ACCENT[accent];
  return (
    <div className="rounded-2xl border border-[var(--c-line)] bg-[var(--c-bg-veil)] p-4">
      <div className="text-xs text-[var(--c-ink-3)]">{label}</div>
      <div className="mt-1 flex items-baseline gap-1">
        <span className={`text-2xl font-bold ${a.text}`}>{pct != null ? pct : "\u00b7"}</span>
        {pct != null && <span className={`text-sm font-semibold ${a.text}`}>%</span>}
      </div>
      <div className="mt-2 h-1.5 rounded-full bg-[var(--c-sunken)] overflow-hidden">
        <div
          className={`h-full rounded-full bg-gradient-to-r ${a.bar}`}
          style={{ width: `${pct ?? 0}%` }}
        />
      </div>
      <div className="mt-2 text-[11px] text-[var(--c-ink-3)]">{detail}</div>
    </div>
  );
}

/* ===================== FUNNEL PIPELINE (the chevron graphic) ===================== */
// The at-a-glance funnel: acquisition, then a fork into a guild lane (the
// leader journey) and a player lane (regular usage). Restored from the
// original design because it's the clearest single-glance read of the funnel.

const STEP_HELP: Record<string, string> = {
  homepage_visited: "Unique browsers that loaded the signed-out homepage. Counted once per browser session via an anonymous id. Tracking started 16 Jul 2026.",
  signup_started: "Clicks on any 'Sign in with Discord' button, counted server-side when the OAuth redirect starts. Tracking started 16 Jul 2026.",
  account_created: "New accounts created on first Discord login. Backfilled from the full user table, so history is complete.",
  guild_created: "Active guilds created.",
  five_members: "Guilds where 5 distinct players have joined (alts don't count twice). This is the Qualified threshold.",
  availability: "Guilds where at least one member submitted or confirmed weekly availability.",
  roster: "Guilds that generated a suggested roster with ARC at least once. This is the Activated threshold. Tracking started 16 Jul 2026, so it shows 0 for now.",
  raid_created: "Guilds with at least one raid scheduled (ARC commit or manual creation).",
  raid_completed: "Guilds with a raid whose scheduled end time has passed. This is the Successful threshold.",
  second_raid_completed: "Guilds with two raids past their scheduled end time. This is the retention signal.",
  player_availability: "Users who submitted or confirmed weekly availability at least once. History is complete (backfilled).",
  player_joined: "Users in at least one guild with any of their characters. Sits after Raided because many players raid as PUGs before (or without) committing to a guild.",
  player_raided: "Users assigned to at least one raid whose scheduled end has passed, including PUG joins.",
  player_raided_2: "Users who have been in 2 or more completed raids. The player retention signal.",
};

const PIPELINE_ICONS: Record<string, string> = {
  home: "M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75",
  login: "M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9",
  user: "M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z",
  flag: "M3 3v1.5M3 21v-6m0 0l2.77-.693a9 9 0 016.208.682l.108.054a9 9 0 006.086.71l3.114-.732a48.524 48.524 0 01-.005-10.499l-3.11.732a9 9 0 01-6.085-.711l-.108-.054a9 9 0 00-6.208-.682L3 4.5M3 15V4.5",
  users: "M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z",
  calendar: "M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5",
  roster: "M9 12h6m-6 4h6M9 8h6M5.25 3h13.5a1.5 1.5 0 011.5 1.5v15a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-15a1.5 1.5 0 011.5-1.5z",
  shield: "M12 3l7.5 3v5.25c0 4.5-3 8.25-7.5 9.75-4.5-1.5-7.5-5.25-7.5-9.75V6l7.5-3z",
  trophy: "M8 4h8v3a4 4 0 11-8 0V4zm0 1H5a3 3 0 003 4m8-4h3a3 3 0 01-3 4m-4 2v4m-3 6h6m-4.5-6h3l.75 6h-4.5l.75-6z",
  repeat: "M4.5 12a7.5 7.5 0 0112.8-5.3l2.2 2.05m0 0V4.5m0 4.25h-4.25M19.5 12a7.5 7.5 0 01-12.8 5.3l-2.2-2.05m0 0v4.25m0-4.25h4.25",
};

type PipeAccent = "indigo" | "cyan" | "amber" | "violet";
type PipeStepDef = { key: string; short: string; icon: string };
type PipeCard = PipeStepDef & { count: number; muted: boolean };

const ACQ_LANE: PipeStepDef[] = [
  { key: "homepage_visited", short: "Homepage", icon: "home" },
  { key: "signup_started", short: "Signup", icon: "login" },
  { key: "account_created", short: "Account", icon: "user" },
];

const GUILD_LANE_PHASES: { label: string; accent: PipeAccent; steps: PipeStepDef[] }[] = [
  {
    label: "Guild building",
    accent: "cyan",
    steps: [
      { key: "guild_created", short: "Guild", icon: "flag" },
      { key: "five_members", short: "5 Players", icon: "users" },
      { key: "availability", short: "Availability", icon: "calendar" },
    ],
  },
  {
    label: "Raiding",
    accent: "amber",
    steps: [
      { key: "roster", short: "Roster", icon: "roster" },
      { key: "raid_created", short: "Raid", icon: "shield" },
      { key: "raid_completed", short: "Raid Done", icon: "trophy" },
      { key: "second_raid_completed", short: "Retained", icon: "repeat" },
    ],
  },
];

const PLAYER_LANE: { label: string; accent: PipeAccent; steps: PipeStepDef[] } = {
  label: "Player journey",
  accent: "violet",
  steps: [
    { key: "player_availability", short: "Availability", icon: "calendar" },
    { key: "player_raided", short: "Raided", icon: "shield" },
    { key: "player_joined", short: "In a Guild", icon: "users" },
    { key: "player_raided_2", short: "Raids 2+", icon: "repeat" },
  ],
};

const PIPELINE_ACCENT: Record<string, { icon: string; card: string; bracketBorder: string; bracketText: string }> = {
  indigo: { icon: "text-indigo-700 dark:text-indigo-300", card: "from-indigo-500/25 to-indigo-500/[0.06]", bracketBorder: "border-indigo-400/40", bracketText: "text-indigo-800 dark:text-indigo-200" },
  cyan: { icon: "text-cyan-700 dark:text-cyan-300", card: "from-cyan-500/25 to-cyan-500/[0.06]", bracketBorder: "border-cyan-400/40", bracketText: "text-cyan-800 dark:text-cyan-200" },
  amber: { icon: "text-amber-700 dark:text-amber-300", card: "from-amber-500/25 to-amber-500/[0.06]", bracketBorder: "border-amber-400/40", bracketText: "text-amber-800 dark:text-amber-200" },
  violet: { icon: "text-violet-700 dark:text-violet-300", card: "from-violet-500/25 to-violet-500/[0.06]", bracketBorder: "border-violet-400/40", bracketText: "text-violet-800 dark:text-violet-200" },
};

// % vs the previous tracked card in the same lane; newly tracked (muted)
// cards are skipped in the chain, >100% (mismatched history) is hidden,
// and the first card's conversion belongs to the chip in front of the lane.
function chainPcts(cards: PipeCard[]): (number | null)[] {
  return cards.map((card, i) => {
    if (card.muted || i === 0) return null;
    for (let j = i - 1; j >= 0; j--) {
      if (cards[j].muted) continue;
      if (cards[j].count <= 0) return null;
      const pct = Math.round((card.count / cards[j].count) * 100);
      return pct > 100 ? null : pct;
    }
    return null;
  });
}

function PipeChevron({ card, accent, pct, noNotch }: { card: PipeCard; accent: PipeAccent; pct: number | null; noNotch?: boolean }) {
  const a = PIPELINE_ACCENT[accent];
  return (
    <div
      title={STEP_HELP[card.key]}
      className={`w-[104px] py-2.5 pr-5 ${noNotch ? "pl-3" : "pl-5"} bg-gradient-to-b ${a.card} flex flex-col items-center justify-center text-center cursor-help ${card.muted ? "opacity-40" : ""}`}
      style={{
        clipPath: noNotch
          ? "polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%)"
          : "polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%, 12px 50%)",
      }}
    >
      <svg className={`w-6 h-6 mb-1 ${a.icon}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6}>
        <path strokeLinecap="round" strokeLinejoin="round" d={PIPELINE_ICONS[card.icon]} />
      </svg>
      <div className="text-base font-bold text-[var(--c-ink)] leading-none">{card.count}</div>
      <div className="text-[10px] text-[var(--c-ink-3)] mt-1 leading-tight">{card.short}</div>
      <div className="text-[9px] mt-0.5 leading-none h-2.5">
        {card.muted ? (
          <span className="text-[var(--c-ink-3)] font-semibold">new</span>
        ) : pct != null ? (
          <span className="text-[var(--c-ink-3)]">{pct}%</span>
        ) : null}
      </div>
    </div>
  );
}

// A phase: colored bracket on top, chevron cards below.
function PipePhase({ label, accent, cards, pcts, noFirstNotch }: {
  label: string;
  accent: PipeAccent;
  cards: PipeCard[];
  pcts: (number | null)[];
  noFirstNotch?: boolean;
}) {
  const a = PIPELINE_ACCENT[accent];
  return (
    <div className="flex flex-col shrink-0">
      <div className={`text-center text-xs font-bold mb-1 ${a.bracketText}`}>{label}</div>
      <div className={`h-2 border-t-2 border-l-2 border-r-2 rounded-t mx-1 mb-2 ${a.bracketBorder}`} />
      <div className="flex-1 flex items-stretch gap-1">
        {cards.map((card, i) => (
          <PipeChevron key={card.key} card={card} accent={accent} pct={pcts[i]} noNotch={noFirstNotch && i === 0} />
        ))}
      </div>
    </div>
  );
}

// Green conversion chip. tilt angles the arrow into a fork branch;
// withHeader adds an invisible spacer matching a phase bracket's height so
// inline chips line up with the cards next to them.
function PipeConv({ pct, tilt, title, withHeader }: { pct: number | null; tilt?: "up" | "down"; title: string; withHeader?: boolean }) {
  const rotate = tilt === "up" ? "-rotate-[25deg]" : tilt === "down" ? "rotate-[25deg]" : "";
  return (
    <div className="flex flex-col shrink-0" title={title}>
      {withHeader && (
        <>
          <div className="invisible text-xs font-bold mb-1">x</div>
          <div className="h-2 mb-2" />
        </>
      )}
      <div className="flex-1 flex flex-col items-center justify-center px-0.5 cursor-help">
        <svg className={`w-4 h-4 text-emerald-700 dark:text-emerald-400 ${rotate}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12l-7.5 7.5M3 12h18" />
        </svg>
        <span className="text-[10px] font-bold text-emerald-700 dark:text-emerald-400 mt-0.5">{pct != null ? `${pct}%` : "\u00b7"}</span>
      </div>
    </div>
  );
}

function FunnelPipeline({ steps, players }: { steps: FunnelStep[]; players?: FunnelPlayers }) {
  const byKey: Record<string, FunnelStep | undefined> = {};
  for (const s of steps) byKey[s.key] = s;
  const count = (key: string) => byKey[key]?.count ?? 0;

  const mkCards = (defs: PipeStepDef[], counts?: Record<string, number>): PipeCard[] =>
    defs.map((d) => ({
      ...d,
      count: counts ? counts[d.key] ?? 0 : count(d.key),
      muted: NO_HISTORY_STEPS.has(d.key),
    }));

  const acqCards = mkCards(ACQ_LANE);
  const acqPcts = chainPcts(acqCards);
  const accounts = count("account_created");

  // Guild lane: one % chain across both phases, chip between them owns the
  // cross-phase conversion so the phase-opening card stays clean.
  const guildPhaseCards = GUILD_LANE_PHASES.map((p) => mkCards(p.steps));
  const guildFlat = guildPhaseCards.flat();
  const guildFlatPcts = chainPcts(guildFlat);
  const raidingStart = guildPhaseCards[0].length;
  const raidingChipIdx = guildFlat.findIndex((c, i) => i >= raidingStart && !c.muted);
  const raidingChipPct = raidingChipIdx >= 0 ? guildFlatPcts[raidingChipIdx] : null;
  const guildPhasePcts = guildPhaseCards.map((cards, pi) => {
    const offset = pi === 0 ? 0 : raidingStart;
    return cards.map((c, i) => {
      const flatIdx = offset + i;
      return flatIdx === raidingChipIdx ? null : guildFlatPcts[flatIdx];
    });
  });

  // Player lane: regular usage, independent of leading a guild.
  const playerCards = mkCards(PLAYER_LANE.steps, players && {
    player_availability: players.availability,
    player_joined: players.joined_guild,
    player_raided: players.raided,
    player_raided_2: players.raided_again,
  });
  const playerPcts = chainPcts(playerCards);

  const forkGuildPct = convPct(count("guild_created"), accounts);
  const forkPlayerPct = players ? convPct(players.availability, accounts) : null;

  return (
    <div className="rounded-2xl border border-[var(--c-line)] bg-[var(--c-bg-veil)] p-5">
      <div className="text-sm font-semibold text-[var(--c-ink)] mb-4 flex items-center">
        Funnel pipeline
        <Info text="The funnel as one visual pipeline. After Acquisition it forks: the top lane follows guilds (the leader journey), the bottom lane follows individual players, so a big account count with few guilds still shows healthy player usage. Green chips are the conversions into and between phases." wide />
      </div>
      <div className="overflow-x-auto pb-1">
        <div className="flex items-center gap-1.5 w-max mx-auto">
          <PipePhase label="Acquisition" accent="indigo" cards={acqCards} pcts={acqPcts} noFirstNotch />
          <div className="flex flex-col justify-around self-stretch gap-2">
            <PipeConv pct={forkGuildPct} tilt="up" title="Share of accounts that created a guild" />
            <PipeConv pct={forkPlayerPct} tilt="down" title="Share of accounts that set availability (the start of the player journey)" />
          </div>
          <div className="flex flex-col gap-4">
            <div className="flex items-stretch gap-1.5">
              {GUILD_LANE_PHASES.map((phase, pi) => (
                <React.Fragment key={phase.label}>
                  {pi > 0 && (
                    <PipeConv pct={raidingChipPct} title="Guilds reaching the raiding phase, vs the previous tracked step" withHeader />
                  )}
                  <PipePhase label={phase.label} accent={phase.accent} cards={guildPhaseCards[pi]} pcts={guildPhasePcts[pi]} />
                </React.Fragment>
              ))}
            </div>
            <div className="flex items-stretch gap-1.5">
              <PipePhase label={PLAYER_LANE.label} accent={PLAYER_LANE.accent} cards={playerCards} pcts={playerPcts} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ===================== STEP FUNNELS (readable per-step breakdown) ===================== */
// Complements the pipeline graphic: same milestones, but as labelled bars with
// exact per-step conversion, easier to read a specific number off of.

interface FlowStep {
  key: string;
  label: string;
  accent: Accent;
  help?: string;
}

const ACQUISITION_FLOW: FlowStep[] = [
  { key: "homepage_visited", label: "Visited the site", accent: "sky", help: "Unique browsers that opened the signed-out homepage. Tracking started 16 Jul 2026." },
  { key: "signup_started", label: "Started sign-up", accent: "sky", help: "Clicked a 'Sign in with Discord' button. Tracking started 16 Jul 2026." },
  { key: "account_created", label: "Created an account", accent: "indigo", help: "Accounts created on first Discord login. History is complete (backfilled)." },
];

const GUILD_FLOW: FlowStep[] = [
  { key: "guild_created", label: "Created a guild", accent: "indigo", help: "Active guilds created." },
  { key: "five_members", label: "Grew to 5 players", accent: "cyan", help: "5 distinct players joined (alts don't count twice). The 'qualified' threshold." },
  { key: "availability", label: "Set availability", accent: "cyan", help: "At least one member submitted or confirmed weekly availability." },
  { key: "roster", label: "Built a roster", accent: "amber", help: "Generated a suggested roster with ARC. Tracking started 16 Jul 2026." },
  { key: "raid_created", label: "Scheduled a raid", accent: "amber", help: "At least one raid scheduled." },
  { key: "raid_completed", label: "Completed a raid", accent: "emerald", help: "A raid whose scheduled end time has passed. The 'successful' threshold." },
  { key: "second_raid_completed", label: "Came back for more", accent: "violet", help: "Completed a second raid. This is the retention signal." },
];

const PLAYER_FLOW: FlowStep[] = [
  { key: "player_availability", label: "Set their availability", accent: "cyan" },
  { key: "player_raided", label: "Raided", accent: "amber" },
  { key: "player_joined", label: "Joined a guild", accent: "indigo" },
  { key: "player_raided_2", label: "Raided again", accent: "violet" },
];

// One horizontal funnel row: label, proportional bar, count, and the
// conversion vs the previous step that actually has history.
function FunnelRow({
  step,
  count,
  prevCount,
  max,
  muted,
}: {
  step: FlowStep;
  count: number;
  prevCount: number | null;
  max: number;
  muted: boolean;
}) {
  const a = ACCENT[step.accent];
  const width = Math.max(count > 0 ? 4 : 0, (count / max) * 100);
  const pct = muted || prevCount == null ? null : convPct(count, prevCount);
  return (
    <div className={`flex items-center gap-3 ${muted ? "opacity-45" : ""}`}>
      <div className="w-40 shrink-0 flex items-center text-sm text-[var(--c-ink-2)]">
        <span className={`w-2 h-2 rounded-full mr-2 shrink-0 ${a.dot}`} />
        <span className="truncate">{step.label}</span>
        {step.help && <Info text={step.help} />}
      </div>
      <div className="flex-1 h-8 rounded-lg bg-[var(--c-sunken)] overflow-hidden">
        <div
          className={`h-full rounded-lg bg-gradient-to-r ${a.bar} transition-all`}
          style={{ width: `${width}%` }}
        />
      </div>
      <div className="w-14 shrink-0 text-right text-lg font-bold text-[var(--c-ink)] tabular-nums">
        {fmtNum(count)}
      </div>
      <div className="w-16 shrink-0 text-right text-xs">
        {muted ? (
          <span className="text-[var(--c-ink-3)] font-semibold">new</span>
        ) : pct != null ? (
          <span className="text-emerald-700 dark:text-emerald-400 font-semibold">{pct}%</span>
        ) : (
          <span className="text-[var(--c-ink-4)]">&middot;</span>
        )}
      </div>
    </div>
  );
}

function Funnel({
  steps,
  counts,
}: {
  steps: FlowStep[];
  counts: Record<string, number>;
}) {
  const max = Math.max(1, ...steps.map((s) => counts[s.key] ?? 0));
  // Previous non-muted step's count, for conversion.
  const prevCounts: (number | null)[] = steps.map((s, i) => {
    for (let j = i - 1; j >= 0; j--) {
      if (!NO_HISTORY_STEPS.has(steps[j].key)) return counts[steps[j].key] ?? 0;
    }
    return null;
  });
  return (
    <div className="space-y-2">
      {steps.map((s, i) => (
        <FunnelRow
          key={s.key}
          step={s}
          count={counts[s.key] ?? 0}
          prevCount={prevCounts[i]}
          max={max}
          muted={NO_HISTORY_STEPS.has(s.key)}
        />
      ))}
    </div>
  );
}

/* ===================== STAGE BADGE ===================== */

const STAGE_META: Record<string, { label: string; accent: Accent; order: number }> = {
  signup: { label: "Signed up", accent: "sky", order: 0 },
  qualified: { label: "Qualified", accent: "cyan", order: 1 },
  activated: { label: "Activated", accent: "amber", order: 2 },
  successful: { label: "Raiding", accent: "emerald", order: 3 },
  retained: { label: "Retained", accent: "violet", order: 4 },
};

function StageBadge({ stage }: { stage: string }) {
  const m = STAGE_META[stage] || STAGE_META.signup;
  const a = ACCENT[m.accent];
  return (
    <span
      className={`inline-flex items-center gap-1.5 border rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${a.soft}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${a.dot}`} />
      {m.label}
    </span>
  );
}

/**
 * Region, realm and faction, shown wherever a guild is named.
 *
 * Not decoration: candidates for a raid are hard-filtered on all three, so they
 * decide who can ever raid together, and region additionally decides the weekly
 * reset day (EU Wednesday, US Tuesday). A guild name alone is ambiguous.
 */
interface GuildIdentityProps {
  region?: string | null;
  serverName?: string | null;
  faction?: string | null;
  className?: string;
}

function GuildIdentity({ region, serverName, faction, className = "" }: GuildIdentityProps) {
  if (!region && !serverName && !faction) return null;
  const factionTone =
    faction === "Alliance"
      ? "text-sky-700 dark:text-sky-300"
      : faction === "Horde"
        ? "text-red-700 dark:text-red-300"
        : "text-[var(--c-ink-3)]";
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[11px] leading-none ${className}`}
      title={`${region || "?"} · ${serverName || "no realm"} · ${faction || "no faction"}`}
    >
      <span className="rounded bg-[var(--c-sunken-2)] px-1.5 py-0.5 font-semibold text-[var(--c-ink-2)]">
        {region || "·"}
      </span>
      <span className="text-[var(--c-ink-3)] truncate">{serverName || "·"}</span>
      <span className={`font-medium ${factionTone}`}>{faction || "·"}</span>
    </span>
  );
}

/** Guild id -> identity, so funnel rows (which carry neither) can show it too. */
type GuildMetaMap = Record<string, { region: string | null; server_name: string | null; faction: string | null }>;

function buildGuildMetaMap(guilds: GuildStat[]): GuildMetaMap {
  const out: GuildMetaMap = {};
  for (const g of guilds) {
    out[g.id] = { region: g.region, server_name: g.server_name, faction: g.faction };
  }
  return out;
}

/* ===================== ICONS ===================== */

const I = {
  users: "M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z",
  flag: "M3 3v1.5M3 21v-6m0 0l2.77-.693a9 9 0 016.208.682l.108.054a9 9 0 006.086.71l3.114-.732a48.524 48.524 0 01-.005-10.499l-3.11.732a9 9 0 01-6.085-.711l-.108-.054a9 9 0 00-6.208-.682L3 4.5M3 15V4.5",
  user: "M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z",
  trophy: "M16.5 18.75h-9m9 0a3 3 0 013 3h-15a3 3 0 013-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 01-.982-3.172M9.497 14.25a7.454 7.454 0 00.981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 007.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M7.73 9.728a6.726 6.726 0 002.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 012.916.52 6.003 6.003 0 01-5.395 4.972m0 0a6.726 6.726 0 01-2.749 1.35m0 0a6.772 6.772 0 01-3.044 0",
  bolt: "M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z",
  eye: "M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z",
  eyeInner: "M15 12a3 3 0 11-6 0 3 3 0 016 0z",
  search: "M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z",
  refresh: "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15",
  discord: "M9 12h6m-6 4h6M9 8h6M5.25 3h13.5a1.5 1.5 0 011.5 1.5v15a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-15a1.5 1.5 0 011.5-1.5z",
  shield: "M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z",
  clock: "M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z",
  warn: "M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z",
};

function Icon({ d, className }: { d: string | string[]; className?: string }) {
  const paths = Array.isArray(d) ? d : [d];
  return (
    <svg className={className || "w-4 h-4"} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      {paths.map((p, i) => (
        <path key={i} strokeLinecap="round" strokeLinejoin="round" d={p} />
      ))}
    </svg>
  );
}

/* ===================== PER-GUILD FUNNEL TABLE ===================== */
// Every guild's milestone timeline, so you can see exactly where each one is
// and which ones stalled. Stalled (pre-activation, no progress for 3+ days)
// float to the top with a flag.

// Stalled = active, pre-activation guild with no forward movement for 3+ days.
function stalledDays(g: FunnelGuild): number | null {
  if (!g.is_active) return null;
  if (g.stage === "activated" || g.stage === "successful" || g.stage === "retained") return null;
  const d = daysSince(g.last_activity_at || g.created_at);
  return d != null && d >= 3 ? d : null;
}

// Milestone cell: date plus days since guild creation, so time-between-steps
// is readable per guild at a glance.
function MilestoneCell({ at, createdAt }: { at: string | null; createdAt: string }) {
  if (!at) return <td className="px-3 py-2.5 text-center text-[var(--c-ink-4)]">{"\u00b7"}</td>;
  const days = (new Date(at).getTime() - new Date(createdAt).getTime()) / 86400000;
  const dayLabel = days < 0 ? "" : days < 1 ? "day 1" : `+${Math.floor(days)}d`;
  return (
    <td className="px-3 py-2.5 text-center whitespace-nowrap">
      <div className="text-[var(--c-ink-2)] text-xs">{formatDate(at)}</div>
      {dayLabel && <div className="text-[10px] text-[var(--c-ink-4)]">{dayLabel}</div>}
    </td>
  );
}

const FUNNEL_COLUMNS = [
  "Guild",
  "Stage",
  "Players",
  "Created",
  "5 Players",
  "Availability",
  "Roster",
  "1st Raid Done",
  "2nd Raid Done",
  "Last Activity",
];

function GuildFunnelTable({ guilds, metaById = {} }: { guilds: FunnelGuild[]; metaById?: GuildMetaMap }) {
  const [stageFilter, setStageFilter] = useState<string | null>(null);

  const stalledCount = guilds.filter((g) => stalledDays(g) != null).length;

  const visible = guilds.filter((g) => (stageFilter ? g.stage === stageFilter : true));
  const sorted = [...visible].sort((a, b) => {
    if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
    const sa = stalledDays(a);
    const sb = stalledDays(b);
    if ((sa != null) !== (sb != null)) return sa != null ? -1 : 1;
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  const stageChips = ["qualified", "activated", "successful", "retained"];

  return (
    <Panel
      title="Per-guild funnel"
      hint="Each guild's milestone timeline. +Nd on a milestone means days after the guild was created. Rows shaded amber/red have stalled before activating."
      right={
        stalledCount > 0 ? (
          <span className="text-xs font-semibold text-amber-700 dark:text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-2.5 py-1">
            {stalledCount} stalled guild{stalledCount === 1 ? "" : "s"} need a look
          </span>
        ) : undefined
      }
    >
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="text-xs text-[var(--c-ink-3)]">Filter stage:</span>
        {stageChips.map((s) => {
          const m = STAGE_META[s];
          const on = stageFilter === s;
          return (
            <button
              key={s}
              onClick={() => setStageFilter(on ? null : s)}
              className={`px-2.5 py-1 rounded-full border text-[11px] font-semibold transition ${
                on
                  ? ACCENT[m.accent].soft + " ring-1 " + ACCENT[m.accent].ring
                  : "border-[var(--c-line)] text-[var(--c-ink-3)] hover:text-[var(--c-ink-2)]"
              }`}
            >
              {m.label}
            </button>
          );
        })}
        {stageFilter && (
          <button
            onClick={() => setStageFilter(null)}
            className="text-[11px] text-[var(--c-ink-3)] hover:text-[var(--c-ink-2)] underline"
          >
            clear
          </button>
        )}
      </div>

      <div className="overflow-x-auto -mx-2">
        <table className="w-full text-sm">
          <thead className="border-b border-[var(--c-line)]">
            <tr>
              {FUNNEL_COLUMNS.map((h) => (
                <th
                  key={h}
                  className="px-3 py-2 text-left text-xs font-semibold text-[var(--c-ink-3)] whitespace-nowrap"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--c-line-soft)]">
            {sorted.length === 0 && (
              <tr>
                <td colSpan={FUNNEL_COLUMNS.length} className="text-center text-[var(--c-ink-4)] py-8">
                  No guilds{stageFilter ? " in this stage" : ""}.
                </td>
              </tr>
            )}
            {sorted.map((g) => {
              const stalled = stalledDays(g);
              const lastActivity = g.last_activity_at || g.created_at;
              return (
                <tr
                  key={g.guild_id}
                  className={`transition-colors ${!g.is_active ? "opacity-40" : ""} ${
                    stalled != null
                      ? stalled >= 7
                        ? "bg-rose-500/[0.06] hover:bg-rose-500/10"
                        : "bg-amber-500/[0.05] hover:bg-amber-500/10"
                      : "hover:bg-[var(--c-bg-veil)]"
                  }`}
                >
                  <td className="px-3 py-2.5 font-medium text-[var(--c-ink)] whitespace-nowrap">
                    {g.name}
                    {g.tag && <span className="ml-1.5 text-[var(--c-ink-4)] text-xs">[{g.tag}]</span>}
                    {!g.is_active && (
                      <span className="ml-1.5 text-[var(--c-ink-4)] text-[10px]">inactive</span>
                    )}
                    {stalled != null && (
                      <span
                        className={`ml-2 text-[10px] font-semibold ${
                          stalled >= 7 ? "text-rose-600 dark:text-rose-400" : "text-amber-700 dark:text-amber-400"
                        }`}
                        title={`No funnel progress for ${Math.floor(stalled)} days`}
                      >
                        stalled {Math.floor(stalled)}d
                      </span>
                    )}
                    <div className="mt-1">
                      <GuildIdentity
                        region={metaById[g.guild_id]?.region}
                        serverName={metaById[g.guild_id]?.server_name}
                        faction={metaById[g.guild_id]?.faction}
                      />
                    </div>
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <StageBadge stage={g.stage} />
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <span className={g.members_joined >= 5 ? "text-cyan-700 dark:text-cyan-300 font-medium" : "text-[var(--c-ink-3)]"}>
                      {g.members_joined}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-[var(--c-ink-3)] whitespace-nowrap text-xs">
                    {formatDate(g.created_at)}
                  </td>
                  <MilestoneCell at={g.fifth_join_at} createdAt={g.created_at} />
                  <MilestoneCell at={g.availability_at} createdAt={g.created_at} />
                  <MilestoneCell at={g.roster_at} createdAt={g.created_at} />
                  <MilestoneCell at={g.first_completed_at} createdAt={g.created_at} />
                  <MilestoneCell at={g.second_completed_at} createdAt={g.created_at} />
                  <td className="px-3 py-2.5 text-[var(--c-ink-3)] whitespace-nowrap text-xs">
                    {timeAgo(lastActivity)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

/* ===================== GROWTH VIEW ===================== */

function GrowthView({
  guilds,
  funnel,
  onViewPlanner,
}: {
  guilds: GuildStat[];
  funnel: FunnelData | null;
  onViewPlanner?: (g: GuildStat) => void;
}) {
  if (!funnel) {
    return <div className="text-center text-[var(--c-ink-3)] py-16">{"Loading growth data\u2026"}</div>;
  }

  const stepCount: Record<string, number> = {};
  for (const s of funnel.steps) stepCount[s.key] = s.count;

  const playerCounts: Record<string, number> = {
    player_availability: funnel.players.availability,
    player_raided: funnel.players.raided,
    player_joined: funnel.players.joined_guild,
    player_raided_2: funnel.players.raided_again,
  };

  const t = funnel.tiers;
  // Funnel rows carry no realm/faction/region, so map them from the guild stats
  // we already fetched rather than widening the funnel view.
  const metaById = buildGuildMetaMap(guilds);

  const totalGuilds = guilds.length;
  const activeGuilds = guilds.filter((g) => g.has_recent_activity).length;
  const totalRaids = guilds.reduce((s, g) => s + g.raid_count, 0);
  const completedRaidGuilds = t.successful;
  const totalMemberships = guilds.reduce((s, g) => s + g.member_count, 0);
  const guildsWithDiscord = guilds.filter((g) => g.discord_bot_enabled).length;
  const guildsUsingSR = guilds.filter((g) => g.soft_reserve_count > 0).length;
  const publishedRaids = guilds.reduce((s, g) => s + g.published_raid_count, 0);

  // Headline conversion rates a CMO cares about.
  const guildToRaiding = convPct(t.successful, t.guilds);
  const retention = convPct(t.retained, t.successful);

  // Time-to-value: pull the friendliest few timings.
  const timingByKey: Record<string, FunnelTiming> = {};
  for (const tm of funnel.timings) timingByKey[tm.key] = tm;
  const ttv: { label: string; key: string; hero?: boolean }[] = [
    { label: "Sign-up \u2192 first guild", key: "signup_to_guild" },
    { label: "Guild \u2192 first player", key: "guild_to_first_member" },
    { label: "1st \u2192 5th player", key: "first_to_fifth" },
    { label: "Guild \u2192 availability", key: "guild_to_availability" },
    { label: "Availability \u2192 roster", key: "availability_to_roster" },
    { label: "Roster \u2192 first raid", key: "roster_to_first_raid" },
    { label: "Guild \u2192 first raid done", key: "guild_to_first_completed", hero: true },
    { label: "1st \u2192 2nd raid", key: "first_to_second_completed" },
  ];

  // Highlights.
  const topGuilds = [...guilds]
    .sort((a, b) => b.member_count - a.member_count || b.raid_count - a.raid_count)
    .slice(0, 5);

  const stageByGuild: Record<string, string> = {};
  for (const fg of funnel.guilds) stageByGuild[fg.guild_id] = fg.stage;

  const stalled = funnel.guilds
    .filter((g) => {
      if (!g.is_active) return false;
      if (["activated", "successful", "retained"].includes(g.stage)) return false;
      const d = daysSince(g.last_activity_at || g.created_at);
      return d != null && d >= 3;
    })
    .map((g) => ({ g, days: daysSince(g.last_activity_at || g.created_at) || 0 }))
    .sort((a, b) => b.days - a.days)
    .slice(0, 5);

  return (
    <div className="space-y-6">
      {/* Headline KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi
          label="Sign-ups"
          value={fmtNum(t.signups)}
          sub="Discord accounts created"
          accent="indigo"
          icon={<Icon d={I.user} />}
        />
        <Kpi
          label="Guilds"
          value={fmtNum(totalGuilds)}
          sub={
            <span className="text-emerald-700/80 dark:text-emerald-400/80">
              {activeGuilds} active this month
            </span>
          }
          accent="cyan"
          icon={<Icon d={I.flag} />}
        />
        <Kpi
          label="Players in guilds"
          value={fmtNum(funnel.players.joined_guild)}
          sub="Distinct people who joined"
          accent="violet"
          icon={<Icon d={I.users} />}
        />
        <Kpi
          label="Raids run"
          value={fmtNum(totalRaids)}
          sub={`${completedRaidGuilds} guilds have raided`}
          accent="amber"
          icon={<Icon d={I.trophy} />}
        />
      </div>

      {/* Secondary totals + feature adoption */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MiniStat
          label="Guild memberships"
          value={fmtNum(totalMemberships)}
          accent="cyan"
          icon={<Icon d={I.users} className="w-5 h-5" />}
        />
        <MiniStat
          label="Published raids"
          value={fmtNum(publishedRaids)}
          accent="amber"
          icon={<Icon d={I.trophy} className="w-5 h-5" />}
        />
        <MiniStat
          label="Guilds on Discord bot"
          value={`${guildsWithDiscord} / ${totalGuilds}`}
          accent="violet"
          icon={<Icon d={I.discord} className="w-5 h-5" />}
        />
        <MiniStat
          label="Guilds using soft reserve"
          value={`${guildsUsingSR} / ${totalGuilds}`}
          accent="rose"
          icon={<Icon d={I.shield} className="w-5 h-5" />}
        />
      </div>

      {/* Conversion rates */}
      <Panel
        title="How the product converts"
        hint="Most players are never expected to found a guild, so we lead with the share who get into a guild at all (founding one or joining one), then track guilds into raiding teams and first raids into repeat raids. Founders are called out on their own because that small group carries the whole system."
      >
        <div className="space-y-4">
          <GuildActivationCard
            signups={t.signups}
            founders={t.guilds}
            members={funnel.players.joined_guild}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <RateCard
              label={"Guild \u2192 completes a raid"}
              pct={guildToRaiding}
              detail={`${fmtNum(t.successful)} of ${fmtNum(t.guilds)} guilds`}
              accent="emerald"
            />
            <RateCard
              label={"Raided \u2192 raids again (retention)"}
              pct={retention}
              detail={`${fmtNum(t.retained)} of ${fmtNum(t.successful)} raiding guilds`}
              accent="violet"
            />
          </div>
        </div>
      </Panel>

      {/* The chevron pipeline: acquisition -> fork into guild + player lanes */}
      <FunnelPipeline steps={funnel.steps} players={funnel.players} />

      {/* Step-by-step funnels: the same milestones as exact, readable bars */}
      <div className="grid lg:grid-cols-2 gap-5">
        <Panel
          title="Acquisition"
          hint="From landing on the site to creating an account. Homepage and sign-up tracking started 16 Jul 2026; accounts are complete (backfilled)."
        >
          <Funnel steps={ACQUISITION_FLOW} counts={stepCount} />
        </Panel>
        <Panel
          title="Guild activation"
          hint={"How far guilds get: 5 players (qualified) \u2192 roster (activated) \u2192 a completed raid (successful) \u2192 a second raid (retained)."}
        >
          <Funnel steps={GUILD_FLOW} counts={stepCount} />
        </Panel>
      </div>

      <Panel
        title="Player journey"
        hint="Individual players, whether or not they lead a guild. Many raid as PUGs before joining a guild, which is why 'joined a guild' can sit below 'raided'."
      >
        <Funnel steps={PLAYER_FLOW} counts={playerCounts} />
      </Panel>

      {/* Time to value */}
      <Panel
        title="Time to value"
        hint="Median time between milestones across guilds. The headline is how long it takes a new guild to complete its first raid."
      >
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {ttv.map((row) => {
            const tm = timingByKey[row.key];
            return (
              <div
                key={row.key}
                className={`rounded-xl border p-4 ${
                  row.hero
                    ? "border-emerald-500/30 bg-emerald-500/[0.06]"
                    : "border-[var(--c-line)] bg-[var(--c-bg-veil)]"
                }`}
              >
                <div className="flex items-center gap-1.5 text-xs text-[var(--c-ink-3)]">
                  <Icon d={I.clock} className="w-3.5 h-3.5" />
                  {row.label}
                </div>
                <div
                  className={`mt-2 text-2xl font-bold ${
                    row.hero ? "text-emerald-700 dark:text-emerald-300" : "text-[var(--c-ink)]"
                  }`}
                >
                  {fmtDuration(tm ? tm.median_hours : null)}
                </div>
                <div className="mt-0.5 text-[11px] text-[var(--c-ink-3)]">
                  {tm && tm.n > 0 ? `from ${tm.n} guild${tm.n === 1 ? "" : "s"}` : "not enough data yet"}
                </div>
              </div>
            );
          })}
        </div>
      </Panel>

      {/* Highlights */}
      <div className="grid lg:grid-cols-2 gap-5">
        <Panel title="Top guilds" hint="Largest guilds by player count. A good shortlist for case studies and community spotlights.">
          {topGuilds.length === 0 ? (
            <div className="text-sm text-[var(--c-ink-3)] py-6 text-center">No guilds yet.</div>
          ) : (
            <ul className="space-y-2">
              {topGuilds.map((g, i) => (
                <li
                  key={g.id}
                  className="flex items-center gap-3 rounded-xl border border-[var(--c-line-soft)] bg-[var(--c-bg-veil)] px-3 py-2.5"
                >
                  <span className="w-6 text-center text-sm font-bold text-[var(--c-ink-4)]">
                    {i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-[var(--c-ink)] truncate">{g.name}</span>
                      {g.tag && <span className="text-[var(--c-ink-4)] text-xs">[{g.tag}]</span>}
                      {stageByGuild[g.id] && <StageBadge stage={stageByGuild[g.id]} />}
                    </div>
                    <div className="text-[11px] text-[var(--c-ink-3)] mt-0.5">
                      {g.member_count} players &middot; {g.raid_count} raids &middot;{" "}
                      {g.game_name || "no game set"}
                    </div>
                    <div className="mt-1">
                      <GuildIdentity region={g.region} serverName={g.server_name} faction={g.faction} />
                    </div>
                  </div>
                  {onViewPlanner && (
                    <ViewAsButton guild={g} onViewPlanner={onViewPlanner} compact />
                  )}
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel
          title="Needs attention"
          hint="Active guilds that qualified but stalled before activating (no funnel progress for 3+ days). Good candidates for a nudge or outreach."
        >
          {stalled.length === 0 ? (
            <div className="flex flex-col items-center justify-center text-center py-6 text-[var(--c-ink-3)]">
              <Icon d={I.trophy} className="w-6 h-6 mb-2 text-emerald-700/60 dark:text-emerald-400/60" />
              <span className="text-sm">Nothing stalled right now.</span>
            </div>
          ) : (
            <ul className="space-y-2">
              {stalled.map(({ g, days }) => (
                <li
                  key={g.guild_id}
                  className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 ${
                    days >= 7
                      ? "border-rose-500/25 bg-rose-500/[0.05]"
                      : "border-amber-500/20 bg-amber-500/[0.04]"
                  }`}
                >
                  <Icon
                    d={I.warn}
                    className={`w-5 h-5 shrink-0 ${days >= 7 ? "text-rose-600 dark:text-rose-400" : "text-amber-700 dark:text-amber-400"}`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-[var(--c-ink)] truncate">{g.name}</span>
                      <StageBadge stage={g.stage} />
                    </div>
                    <div className="text-[11px] text-[var(--c-ink-3)] mt-0.5">
                      {g.members_joined} players &middot; created {timeAgo(g.created_at)}
                    </div>
                    <div className="mt-1">
                      <GuildIdentity
                        region={metaById[g.guild_id]?.region}
                        serverName={metaById[g.guild_id]?.server_name}
                        faction={metaById[g.guild_id]?.faction}
                      />
                    </div>
                  </div>
                  <span
                    className={`text-xs font-semibold shrink-0 ${
                      days >= 7 ? "text-rose-600 dark:text-rose-400" : "text-amber-700 dark:text-amber-400"
                    }`}
                  >
                    stalled {Math.floor(days)}d
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      {/* Per-guild funnel milestone table */}
      <GuildFunnelTable guilds={funnel.guilds} metaById={metaById} />
    </div>
  );
}

/* ===================== VIEW-AS BUTTON ===================== */

function ViewAsButton({
  guild,
  onViewPlanner,
  compact,
}: {
  guild: GuildStat;
  onViewPlanner: (g: GuildStat) => void;
  compact?: boolean;
}) {
  const disabled = !guild.game_id;
  return (
    <button
      onClick={() => onViewPlanner(guild)}
      disabled={disabled}
      title={disabled ? "This guild has no game set" : `Open ${guild.name}'s planner in admin mode`}
      className={`inline-flex items-center gap-1.5 rounded-lg border font-semibold transition disabled:opacity-30 disabled:cursor-not-allowed ${
        compact ? "px-2.5 py-1 text-[11px]" : "px-3 py-1.5 text-xs"
      } bg-indigo-500/15 hover:bg-indigo-500/30 border-indigo-400/40 text-indigo-800 dark:text-indigo-200`}
    >
      <Icon d={[I.eye, I.eyeInner]} className={compact ? "w-3.5 h-3.5" : "w-4 h-4"} />
      View as
    </button>
  );
}

/* ===================== GUILDS DIRECTORY ===================== */

type SortKey =
  | "name"
  | "game_name"
  | "owner_name"
  | "member_count"
  | "character_count"
  | "raid_count"
  | "published_raid_count"
  | "latest_raid_date"
  | "soft_reserve_count"
  | "discord_bot_enabled"
  | "created_at";

type ActivityFilter = "all" | "active" | "dormant";

function GuildsDirectory({
  guilds,
  stageByGuild,
  onViewPlanner,
}: {
  guilds: GuildStat[];
  stageByGuild: Record<string, string>;
  onViewPlanner?: (g: GuildStat) => void;
}) {
  const [search, setSearch] = useState("");
  const [activity, setActivity] = useState<ActivityFilter>("all");
  const [stageFilter, setStageFilter] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>("member_count");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const q = search.trim().toLowerCase();
  const filtered = guilds.filter((g) => {
    if (q) {
      const hay = `${g.name} ${g.tag || ""} ${g.owner_name || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (activity === "active" && !g.has_recent_activity) return false;
    if (activity === "dormant" && g.has_recent_activity) return false;
    if (stageFilter && (stageByGuild[g.id] || "signup") !== stageFilter) return false;
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    const av = a[sortKey];
    const bv = b[sortKey];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === "number" && typeof bv === "number")
      return sortDir === "asc" ? av - bv : bv - av;
    if (typeof av === "boolean" && typeof bv === "boolean")
      return sortDir === "asc" ? +av - +bv : +bv - +av;
    const sa = String(av).toLowerCase();
    const sb = String(bv).toLowerCase();
    return sortDir === "asc" ? sa.localeCompare(sb) : sb.localeCompare(sa);
  });

  const Th = ({ label, field, center }: { label: string; field: SortKey; center?: boolean }) => (
    <th
      onClick={() => toggleSort(field)}
      className={`px-3 py-2.5 text-xs font-semibold text-[var(--c-ink-3)] cursor-pointer hover:text-[var(--c-ink)] select-none whitespace-nowrap ${
        center ? "text-center" : "text-left"
      }`}
    >
      {label}
      <span className={`ml-1 ${sortKey === field ? "text-indigo-700 dark:text-indigo-300" : "text-[var(--c-ink-4)]"}`}>
        {sortKey === field ? (sortDir === "asc" ? "\u25B2" : "\u25BC") : "\u21C5"}
      </span>
    </th>
  );

  const stageChips = ["qualified", "activated", "successful", "retained"];

  return (
    <div className="space-y-4">
      {onViewPlanner && (
        <div className="flex items-start gap-3 rounded-xl border border-indigo-400/20 bg-indigo-500/[0.06] px-4 py-3">
          <Icon d={[I.eye, I.eyeInner]} className="w-5 h-5 text-indigo-700 dark:text-indigo-300 mt-0.5 shrink-0" />
          <p className="text-sm text-[var(--c-ink-2)]">
            <span className="font-semibold text-[var(--c-ink)]">View as guild.</span> Open any guild's
            planner exactly as its members see it, in admin mode. Handy for support and QA. Use the{" "}
            <span className="text-indigo-800 dark:text-indigo-200 font-medium">View as</span> button on any row.
          </p>
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--c-ink-4)]">
            <Icon d={I.search} />
          </span>
          <input
            type="text"
            placeholder={"Search by guild, tag or owner\u2026"}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-[var(--c-sunken)] border border-[var(--c-line)] rounded-lg pl-9 pr-3 py-2 text-sm text-[var(--c-ink)] placeholder-white/30 focus:outline-none focus:border-indigo-500/50"
          />
        </div>
        <div className="flex rounded-lg border border-[var(--c-line)] overflow-hidden">
          {(["all", "active", "dormant"] as ActivityFilter[]).map((f) => (
            <button
              key={f}
              onClick={() => setActivity(f)}
              className={`px-3 py-2 text-xs font-medium capitalize transition ${
                activity === f ? "bg-indigo-500/25 text-[var(--c-ink)]" : "text-[var(--c-ink-3)] hover:text-[var(--c-ink)]"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-[var(--c-ink-3)]">Stage:</span>
        {stageChips.map((s) => {
          const m = STAGE_META[s];
          const on = stageFilter === s;
          return (
            <button
              key={s}
              onClick={() => setStageFilter(on ? null : s)}
              className={`px-2.5 py-1 rounded-full border text-[11px] font-semibold transition ${
                on ? ACCENT[m.accent].soft + " ring-1 " + ACCENT[m.accent].ring : "border-[var(--c-line)] text-[var(--c-ink-3)] hover:text-[var(--c-ink-2)]"
              }`}
            >
              {m.label}
            </button>
          );
        })}
        <span className="ml-auto text-xs text-[var(--c-ink-3)]">
          {sorted.length} of {guilds.length} guild{guilds.length === 1 ? "" : "s"}
        </span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-2xl border border-[var(--c-line)] bg-[var(--c-bg-veil)]">
        <table className="w-full text-sm">
          <thead className="bg-[var(--c-sunken)] border-b border-[var(--c-line)]">
            <tr>
              <Th label="Guild" field="name" />
              <th className="px-3 py-2.5 text-left text-xs font-semibold text-[var(--c-ink-3)]">Stage</th>
              <Th label="Game" field="game_name" />
              <Th label="Owner" field="owner_name" />
              <Th label="Players" field="member_count" center />
              <Th label="Chars" field="character_count" center />
              <Th label="Raids" field="raid_count" center />
              <Th label="Published" field="published_raid_count" center />
              <Th label="Latest raid" field="latest_raid_date" />
              <Th label="SR" field="soft_reserve_count" center />
              <Th label="Discord" field="discord_bot_enabled" center />
              <Th label="Created" field="created_at" />
              {onViewPlanner && (
                <th className="px-3 py-2.5 text-right text-xs font-semibold text-[var(--c-ink-3)]">
                  Action
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--c-line-soft)]">
            {sorted.length === 0 && (
              <tr>
                <td colSpan={onViewPlanner ? 13 : 12} className="text-center text-[var(--c-ink-4)] py-10">
                  No guilds match your filters.
                </td>
              </tr>
            )}
            {sorted.map((g) => (
              <tr
                key={g.id}
                className={`transition-colors hover:bg-[var(--c-bg-veil)] ${
                  !g.is_active ? "opacity-50" : ""
                }`}
              >
                <td className="px-3 py-2.5 font-medium text-[var(--c-ink)] whitespace-nowrap">
                  {g.name}
                  {g.tag && <span className="ml-1.5 text-[var(--c-ink-4)] text-xs">[{g.tag}]</span>}
                  {!g.is_active && (
                    <span className="ml-1.5 text-[var(--c-ink-4)] text-[10px]">inactive</span>
                  )}
                  <div className="mt-1">
                    <GuildIdentity region={g.region} serverName={g.server_name} faction={g.faction} />
                  </div>
                </td>
                <td className="px-3 py-2.5 whitespace-nowrap">
                  <StageBadge stage={stageByGuild[g.id] || "signup"} />
                </td>
                <td className="px-3 py-2.5 text-[var(--c-ink-2)] whitespace-nowrap">
                  {g.game_name || "\u00b7"}
                </td>
                <td className="px-3 py-2.5 text-[var(--c-ink-2)] whitespace-nowrap">
                  {g.owner_name || "\u00b7"}
                </td>
                <td className="px-3 py-2.5 text-center">
                  <span className={g.member_count > 0 ? "text-cyan-700 dark:text-cyan-300 font-medium" : "text-[var(--c-ink-4)]"}>
                    {g.member_count}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-center text-[var(--c-ink-3)]">{g.character_count}</td>
                <td className="px-3 py-2.5 text-center">
                  <span className={g.raid_count > 0 ? "text-amber-700 dark:text-amber-300 font-medium" : "text-[var(--c-ink-4)]"}>
                    {g.raid_count}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-center text-[var(--c-ink-3)]">{g.published_raid_count}</td>
                <td className="px-3 py-2.5 text-[var(--c-ink-3)] whitespace-nowrap">
                  {g.latest_raid_date ? formatDate(g.latest_raid_date) : "\u00b7"}
                </td>
                <td className="px-3 py-2.5 text-center">
                  {g.soft_reserve_count > 0 ? (
                    <span className="text-rose-700 dark:text-rose-300 font-medium">{g.soft_reserve_count}</span>
                  ) : (
                    <span className="text-[var(--c-ink-4)]">{"\u00b7"}</span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-center">
                  <span
                    className={`inline-block w-2.5 h-2.5 rounded-full ${
                      g.discord_bot_enabled ? "bg-emerald-400" : "bg-[var(--c-sunken-2)]"
                    }`}
                    title={g.discord_bot_enabled ? "Bot enabled" : "No bot"}
                  />
                </td>
                <td className="px-3 py-2.5 text-[var(--c-ink-3)] whitespace-nowrap text-xs">
                  {formatDate(g.created_at)}
                </td>
                {onViewPlanner && (
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    <ViewAsButton guild={g} onViewPlanner={onViewPlanner} />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ===================== PAGE ===================== */

type GuildStatsAdminPageProps = {
  onViewPlanner?: (guild: CreateRaidGuild) => void;
};

type View = "growth" | "guilds";

export default function GuildStatsAdminPage({ onViewPlanner }: GuildStatsAdminPageProps) {
  const [view, setView] = useState<View>("growth");
  const [guilds, setGuilds] = useState<GuildStat[]>([]);
  const [funnel, setFunnel] = useState<FunnelData | null>(null);
  const [fetchedAt, setFetchedAt] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [statsRes, funnelRes] = await Promise.all([
        fetch("/admin/guild-stats", { credentials: "include" }),
        fetch("/admin/funnel", { credentials: "include" }),
      ]);
      if (!statsRes.ok) throw new Error(`Guild stats: HTTP ${statsRes.status}`);
      if (!funnelRes.ok) throw new Error(`Funnel: HTTP ${funnelRes.status}`);
      const statsData = await statsRes.json();
      const funnelData: FunnelData = await funnelRes.json();
      setGuilds(statsData.guilds);
      setFetchedAt(statsData.fetched_at);
      setFunnel(funnelData);
    } catch (err: any) {
      setError(err.message || "Failed to load analytics");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  // Adapt a GuildStat into the CreateRaidGuild shape the planner expects.
  const handleViewPlanner = onViewPlanner
    ? (g: GuildStat) =>
        onViewPlanner({
          id: String(g.id),
          name: g.name,
          tag: g.tag,
          serverName: g.server_name,
          region: g.region === "US" ? "US" : "EU",
          logoUrl: g.logo_url,
          gameId: String(g.game_id ?? ""),
        })
    : undefined;

  const stageByGuild = useMemo(() => {
    const map: Record<string, string> = {};
    if (funnel) for (const fg of funnel.guilds) map[fg.guild_id] = fg.stage;
    return map;
  }, [funnel]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-[var(--c-ink)] flex items-center gap-2.5">
            <span className="text-indigo-600 dark:text-indigo-400">
              <Icon d={I.bolt} className="w-6 h-6" />
            </span>
            Analytics
          </h2>
          <p className="text-sm text-[var(--c-ink-3)] mt-1">
            {view === "growth"
              ? "The health of the product at a glance: growth, activation and retention."
              : "Every guild on the platform. Search, sort, and drop into any one to see what they see."}
            {fetchedAt && (
              <span className="text-[var(--c-ink-4)]">
                {" "}
                &middot; updated {timeAgo(fetchedAt)}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Segmented view switch */}
          <div className="flex rounded-xl border border-[var(--c-line)] bg-[var(--c-bg-veil)] p-1">
            {([
              { id: "growth", label: "Growth" },
              { id: "guilds", label: "Guilds" },
            ] as { id: View; label: string }[]).map((tab) => (
              <button
                key={tab.id}
                onClick={() => setView(tab.id)}
                className={`px-4 py-1.5 rounded-lg text-sm font-medium transition ${
                  view === tab.id
                    ? "bg-indigo-500/30 text-[var(--c-ink)] shadow-sm"
                    : "text-[var(--c-ink-3)] hover:text-[var(--c-ink)]"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <button
            onClick={fetchAll}
            disabled={isLoading}
            className="flex items-center gap-1.5 bg-[var(--c-sunken)] hover:bg-[var(--c-sunken-2)] border border-[var(--c-line)] disabled:opacity-50 text-[var(--c-ink)] text-sm font-medium px-3 py-2 rounded-xl transition"
          >
            <Icon d={I.refresh} className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-xl border border-rose-500/30 bg-rose-500/[0.06] px-4 py-3 text-sm text-rose-700 dark:text-rose-300">
          Couldn't load analytics: {error}
          <button onClick={fetchAll} className="ml-3 underline hover:text-[var(--c-ink)]">
            Retry
          </button>
        </div>
      )}

      {/* Loading */}
      {isLoading && !funnel && (
        <div className="text-center text-[var(--c-ink-3)] py-20">{"Loading analytics\u2026"}</div>
      )}

      {/* Body */}
      {!error && (funnel || !isLoading) && (
        <>
          {view === "growth" && (
            <GrowthView guilds={guilds} funnel={funnel} onViewPlanner={handleViewPlanner} />
          )}
          {view === "guilds" && (
            <GuildsDirectory
              guilds={guilds}
              stageByGuild={stageByGuild}
              onViewPlanner={handleViewPlanner}
            />
          )}
        </>
      )}
    </div>
  );
}
